if (!customElements.get("shoppable-video")) {
  customElements.define(
    "shoppable-video",
    class ShoppableVideo extends HTMLElement {
      constructor() {
        super();
        this.playButtonsListeners = [];
        this.windowScrollRaf = null;
        this.productsScrollRaf = null;
        this.resizeRaf = null;
        this.resizeObserver = null;
        this.lastSyncedProductIndex = null;
        this.dominantProductIndex = 0;
        this.scrollTravelDistance = 0;
        this._lastSeekTime = 0;
        this._seekCooldown = 1200;
      }

      bindMethods() {
        this.togglePlayPause = this.togglePlayPause.bind(this);
        this.toggleVideoIcon = this.toggleVideoIcon.bind(this);
        this.playVideo = this.playVideo.bind(this);
        this.handleProductsScroll = this.handleProductsScroll.bind(this);
        this.handleWindowScroll = this.handleWindowScroll.bind(this);
        this.handleResize = this.handleResize.bind(this);
      }

      setupElements() {
        this.productsInfo = JSON.parse(this.dataset.products || "{}");
        this.video = this.querySelector("video");
        this.scrollStage = this.querySelector(".shoppable-video__scroll-stage");
        this.scrollContainer = this.querySelector(".shoppable-video__container");
        this.productsContainer = this.querySelector(".shoppable-video__products");
        this.controlButton = this.querySelector(".shoppable-video__control-button");
        this.products = Array.from(
          this.productsContainer?.querySelectorAll(".shoppable-video__product") || [],
        );
        this.productCards = this.products.filter(
          (product) =>
            !product.classList.contains("shoppable-video__empty-product"),
        );
        this.emptyProduct = this.productsContainer?.querySelector(
          ".shoppable-video__empty-product",
        );
        this.playButtons = this.querySelectorAll(".shoppable-video__rewind-button");
        this.mediaImages = this.querySelectorAll(".shoppable-video__products img");
        this.gap = 0;
      }

      handleVideoEnded() {
        // Video bittiğinde tekrar başlatma — son karede dur
        if (this.video) {
          this.video.pause();
        }
      }

      addEventListeners() {
        this.handleVideoEnded = this.handleVideoEnded.bind(this);
        this.controlButton?.addEventListener("click", this.togglePlayPause);
        this.video?.addEventListener("play", this.toggleVideoIcon);
        this.video?.addEventListener("pause", this.toggleVideoIcon);
        this.video?.addEventListener("ended", this.handleVideoEnded);
        this.productsContainer?.addEventListener("scroll", this.handleProductsScroll, {
          passive: true,
        });
        window.addEventListener("scroll", this.handleWindowScroll, {
          passive: true,
        });
        window.addEventListener("resize", this.handleResize);

        this.playButtons.forEach((btn) => {
          const boundClickRewindButton = this.clickRewindButton.bind(this, btn);
          btn.addEventListener("click", boundClickRewindButton);
          this.playButtonsListeners.push({ btn, boundClickRewindButton });
        });

        this.mediaImages.forEach((image) => {
          if (image.complete) return;
          image.addEventListener("load", this.handleResize, { once: true });
        });

        if ("ResizeObserver" in window && this.scrollContainer) {
          this.resizeObserver = new ResizeObserver(() => {
            this.handleResize();
          });
          this.resizeObserver.observe(this.scrollContainer);
          this.resizeObserver.observe(this.productsContainer);
        }

        this.handleResize();
        this.handleWindowScroll();
        this.handleProductsScroll();
      }

      getProductTimestamp(index) {
        return Number(this.productsInfo[String(index)] ?? this.productsInfo[index]);
      }

      getFirstProductCard() {
        return this.productCards[0] || null;
      }

      getMaxScrollLeft() {
        if (!this.productsContainer) return 0;
        return Math.max(
          this.productsContainer.scrollWidth - this.productsContainer.clientWidth,
          0,
        );
      }

      clamp(value, min = 0, max = 1) {
        return Math.max(min, Math.min(max, value));
      }

      getContainerPaddingStart() {
        if (!this.productsContainer) return 0;
        return (
          parseFloat(
            window.getComputedStyle(this.productsContainer).paddingInlineStart,
          ) || 0
        );
      }

      getProductScrollPosition(product) {
        if (!product) return 0;
        return Math.max(product.offsetLeft - this.getContainerPaddingStart(), 0);
      }

      calculateEmptyProductWidth() {
        const firstProduct = this.getFirstProductCard();
        if (!firstProduct || !this.emptyProduct || !this.productsContainer) return;

        const styles = window.getComputedStyle(this.productsContainer);
        this.gap = parseFloat(styles.getPropertyValue("gap")) || 0;
        const paddingInlineStart = parseFloat(styles.paddingInlineStart) || 0;
        const paddingInlineEnd = parseFloat(styles.paddingInlineEnd) || 0;

        const emptyWidth = Math.max(
          this.productsContainer.clientWidth -
            firstProduct.offsetWidth -
            paddingInlineStart -
            paddingInlineEnd -
            this.gap,
          0,
        );

        this.emptyProduct.style.width = `${emptyWidth}px`;
      }

      calculateScrollStage() {
        if (!this.scrollStage || !this.scrollContainer) return;

        const stickyHeight = this.scrollContainer.offsetHeight;
        const maxScrollLeft = this.getMaxScrollLeft();

        // Scroll mesafesini sınırla — hızlı kaydırmada sayfa sonuna atlamayı önler
        const maxTravel = Math.min(stickyHeight * 1.5, 900);
        this.scrollTravelDistance =
          maxScrollLeft > 0 ? Math.min(maxScrollLeft, maxTravel) : 0;

        this.scrollStage.style.setProperty(
          "--sv-sticky-height",
          `${stickyHeight}px`,
        );
        this.scrollStage.style.setProperty(
          "--sv-scroll-track",
          `${this.scrollTravelDistance}px`,
        );
      }

      handleResize() {
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);

        this.resizeRaf = requestAnimationFrame(() => {
          this.calculateEmptyProductWidth();
          this.calculateScrollStage();
          this.handleWindowScroll();
          this.handleProductsScroll();
        });
      }

      syncProductsToPageScroll() {
        if (!this.scrollStage || !this.productsContainer) return;

        const maxScrollLeft = this.getMaxScrollLeft();
        if (this.scrollTravelDistance <= 0 || maxScrollLeft <= 0) {
          this.productsContainer.scrollLeft = 0;
          return;
        }

        const stageRect = this.scrollStage.getBoundingClientRect();
        const progress = this.clamp(
          -stageRect.top / this.scrollTravelDistance,
          0,
          1,
        );
        const nextScrollLeft = progress * maxScrollLeft;

        if (Math.abs(this.productsContainer.scrollLeft - nextScrollLeft) > 0.5) {
          this.productsContainer.scrollLeft = nextScrollLeft;
        }
      }

      handleWindowScroll() {
        if (this.windowScrollRaf) return;

        this.windowScrollRaf = requestAnimationFrame(() => {
          this.windowScrollRaf = null;
          this.syncProductsToPageScroll();
          this.handleProductsScroll();
        });
      }

      checkDominantProductElement() {
        if (!this.productsContainer || !this.products.length) return 0;

        const containerRect = this.productsContainer.getBoundingClientRect();
        const containerCenter = containerRect.left + containerRect.width / 2;
        let nextIndex = 0;
        let shortestDistance = Infinity;

        this.products.forEach((product, index) => {
          const rect = product.getBoundingClientRect();
          const productCenter = rect.left + rect.width / 2;
          const distance = Math.abs(containerCenter - productCenter);

          if (distance < shortestDistance) {
            shortestDistance = distance;
            nextIndex = index;
          }
        });

        return nextIndex;
      }

      syncVideoToDominantProduct(force = false) {
        // Video scroll sırasında otomatik seek yapılmaz
        // Sadece kullanıcı rewind butonuna tıklarsa (force=true) video atlar
        if (!this.video || !force) return;

        const nextIndex = this.dominantProductIndex || 0;
        const startSecond = this.getProductTimestamp(nextIndex);
        this.lastSyncedProductIndex = nextIndex;

        if (!Number.isFinite(startSecond)) return;

        this.video.currentTime = startSecond;
        this._lastSeekTime = Date.now();
        this.playVideo();
      }

      handleProductsScroll() {
        if (this.productsScrollRaf) return;

        this.productsScrollRaf = requestAnimationFrame(() => {
          this.productsScrollRaf = null;
          const nextDominantProductIndex = this.checkDominantProductElement();

          if (nextDominantProductIndex !== this.dominantProductIndex) {
            this.dominantProductIndex = nextDominantProductIndex;
            clearTimeout(this._videoSyncTimer);
            this._videoSyncTimer = setTimeout(() => {
              if (this.dominantProductIndex === nextDominantProductIndex) {
                this.syncVideoToDominantProduct();
              }
            }, 500);
          }
        });
      }

      clickRewindButton(btn) {
        const productIndex = Number(btn.dataset.productIndex);
        const targetProduct = this.products[productIndex];
        const startSecond = this.getProductTimestamp(productIndex);

        if (targetProduct && this.productsContainer) {
          const nextScrollPosition = this.clamp(
            this.getProductScrollPosition(targetProduct),
            0,
            this.getMaxScrollLeft(),
          );
          this.animateScroll(
            this.productsContainer.scrollLeft,
            nextScrollPosition,
            800,
          );
        }

        if (Number.isFinite(startSecond) && this.video) {
          this.dominantProductIndex = productIndex;
          this.lastSyncedProductIndex = null;
          this.video.currentTime = startSecond;
          this.playVideo();
        }
      }

      togglePlayPause(event) {
        event.preventDefault();
        if (!this.video) return;

        if (this.video.paused || this.video.ended) {
          this.video.play();
        } else {
          this.video.pause();
        }
      }

      playVideo() {
        if (!this.video) return;

        if (this.video.paused || this.video.ended) {
          this.video.play();
        }
      }

      toggleVideoIcon() {
        if (!this.video || !this.controlButton) return;

        const isPlaying = !this.video.paused;
        this.controlButton.classList.toggle(
          "shoppable-video__control-button--play",
          isPlaying,
        );
        this.controlButton.classList.toggle(
          "shoppable-video__control-button--pause",
          !isPlaying,
        );
      }

      animateScroll(startPosition, endPosition, duration) {
        if (!this.productsContainer) return;

        const start = performance.now();

        const animate = (time) => {
          const timeElapsed = time - start;
          const progress = Math.min(timeElapsed / duration, 1);
          const currentPosition =
            startPosition +
            (endPosition - startPosition) * this.easeOutSine(progress);

          this.productsContainer.scrollLeft = currentPosition;

          if (timeElapsed < duration) {
            requestAnimationFrame(animate);
          } else {
            this.productsContainer.scrollLeft = endPosition;
            this.handleProductsScroll();
          }
        };

        requestAnimationFrame(animate);
      }

      easeOutSine(progress) {
        return Math.sin((progress * Math.PI) / 2);
      }

      removeEventListeners() {
        this.controlButton?.removeEventListener("click", this.togglePlayPause);
        this.video?.removeEventListener("play", this.toggleVideoIcon);
        this.video?.removeEventListener("pause", this.toggleVideoIcon);
        this.video?.removeEventListener("ended", this.handleVideoEnded);
        this.productsContainer?.removeEventListener(
          "scroll",
          this.handleProductsScroll,
        );
        window.removeEventListener("scroll", this.handleWindowScroll);
        window.removeEventListener("resize", this.handleResize);

        this.mediaImages.forEach((image) => {
          image.removeEventListener("load", this.handleResize);
        });

        this.playButtonsListeners.forEach(({ btn, boundClickRewindButton }) => {
          btn.removeEventListener("click", boundClickRewindButton);
        });

        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }

        if (this.windowScrollRaf) cancelAnimationFrame(this.windowScrollRaf);
        if (this.productsScrollRaf) cancelAnimationFrame(this.productsScrollRaf);
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      }

      connectedCallback() {
        this.setupElements();
        this.bindMethods();
        this.addEventListeners();
      }

      disconnectedCallback() {
        this.removeEventListeners();
      }
    },
  );
}
